import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Observable, ReplaySubject } from 'rxjs';
import { SelectedSiteService } from '../../selected-site.service';
import { ApiErrorFactory } from '../api-errors';
import {
  PhpVersionResponseJSON,
  PhpVersionStagingResponseJSON,
  PhpVersionStagingState,
  PhpVersionState,
} from './php-version';
@Injectable({
  providedIn: 'root',
})
export class PhpVersionService {
  private siteId: string;
  private loading$$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private phpVersionState$$: ReplaySubject<PhpVersionState> = new ReplaySubject<PhpVersionState>(1);
  private phpVersionStagingState$$: ReplaySubject<PhpVersionStagingState> = new ReplaySubject<PhpVersionStagingState>(
    1,
  );
  private readonly headers = new HttpHeaders().set('Content-Type', 'application/json');
  private readonly siteUrlV2 = '/api/v2/site/';

  constructor(
    private http: HttpClient,
    private selectedSiteService: SelectedSiteService,
    private apiErrorFactory: ApiErrorFactory,
    private alertService: SnackbarService,
  ) {
    this.selectedSiteService.getSiteId().subscribe((siteId: string) => (this.siteId = siteId));
  }

  setPhpVersion(phpVersion): void {
    const body = {
      sitePhpVersion: phpVersion,
    };
    this.loading$$.next(true);
    this.http
      .post<PhpVersionResponseJSON>(this.getPhpVersionUrl(this.siteId), body, {
        headers: this.headers,
      })
      .subscribe(
        (resp) => this.handleSetHttpResponse(resp, phpVersion),
        (err) => this.handleHttpError(err),
      );
  }

  loadPhpVersionState(): void {
    this.http.get<PhpVersionResponseJSON>(this.getPhpVersionUrl(this.siteId)).subscribe(
      (resp) => this.handleHttpResponse(resp),
      (err) => this.handleHttpError(err),
    );
  }

  private handleHttpResponse(response: PhpVersionResponseJSON): void {
    this.phpVersionState$$.next(PhpVersionState.fromJSON(response.data));
    this.loading$$.next(false);
  }

  private handleSetHttpResponse(resp, phpVersion): void {
    if (resp.data.success) {
      this.phpVersionState$$.next(PhpVersionState.fromJSON({ sitePhpVersion: phpVersion }));
    }
    this.loading$$.next(false);
  }

  private handleHttpError(err: HttpErrorResponse): void {
    const parsedError = this.apiErrorFactory.parseErrorResponse(err);
    this.alertService.openErrorSnack(parsedError.getUserMessage());
    this.loading$$.next(false);
  }

  private getPhpVersionUrl(siteId: string): string {
    return this.siteUrlV2 + siteId + '/php-version/';
  }

  public getPhpVersionState$(): Observable<PhpVersionState> {
    return this.phpVersionState$$.asObservable();
  }

  public getLoading$(): Observable<boolean> {
    return this.loading$$.asObservable();
  }

  setPhpVersionStaging(phpVersion): void {
    const body = {
      stagingSitePhpVersion: phpVersion,
    };
    this.loading$$.next(true);
    this.http
      .post<PhpVersionStagingResponseJSON>(this.getPhpVersionStagingUrl(this.siteId), body, {
        headers: this.headers,
      })
      .subscribe(
        (resp) => this.handleSetHttpStagingResponse(resp, phpVersion),
        (err) => this.handleHttpError(err),
      );
  }

  private handleSetHttpStagingResponse(resp, phpVersion): void {
    if (resp.data.success) {
      this.phpVersionStagingState$$.next(PhpVersionStagingState.fromJSON({ stagingSitePhpVersion: phpVersion }));
    }
    this.loading$$.next(false);
  }

  private handleHttpStagingResponse(response: PhpVersionStagingResponseJSON): void {
    this.phpVersionStagingState$$.next(PhpVersionStagingState.fromJSON(response.data));
    this.loading$$.next(false);
  }

  loadPhpVersionStagingState(): void {
    this.http.get<PhpVersionStagingResponseJSON>(this.getPhpVersionStagingUrl(this.siteId)).subscribe(
      (resp) => this.handleHttpStagingResponse(resp),
      (err) => this.handleHttpError(err),
    );
  }

  private getPhpVersionStagingUrl(siteId: string): string {
    return this.siteUrlV2 + siteId + '/staging-php-version/';
  }

  public getPhpVersionStagingState$(): Observable<PhpVersionStagingState> {
    return this.phpVersionStagingState$$.asObservable();
  }
}
